import Box from '@components/base/Box'
import Modal from '@components/base/Modal'
import Accordion from '@components/event/Accordion'
import Card from '@components/event/Card'
import { Button } from '@components/inputs/Button'
import { CiWarning } from 'react-icons/ci'
import { EventType } from '@graphql/__generated/graphql'
import CreateEventLayout from '@layouts/CreateEventLayout'
import { CreateEventStage } from '@libs/CreateEvent/CreateEventStage'
import { CreateEventSection, shouldShowField } from '@libs/Utility/validation'
import AttendeesForm from './attendees'
import BreakoutRoomsForm from './breakout-rooms'
import CommunicationsForm from './communications'
import FundraisingForm from './fundraising'
import FundraisingPickerForm from './fundraisingPicker'
import OndemandContantForm from './on-demand'
import TicketsForm from './registration/registration'
import SessionsForm from './sessions'
import SpeakersForm from './speakers'
import SponsorsForm from './sponsors'
import StagesForm from './stages'
import Text from '@components/base/Text'
import { FaRegCheckCircle, FaSyncAlt } from 'react-icons/fa'
import { CheckboxInput } from '@components/inputs/CheckboxInput'
import NotificationsForm from './notifications'
import DetailsForm from './details'
import { useEventEditor } from '@libs/CreateEvent/useEventEditor'

export default function CreateEventPage() {
  const {
    activeEventId,
    completeSections,
    createEventStage,
    errors,
    eventTypeError,
    formData,
    formState,
    goToDashboard,
    handleNext,
    handleSelectEventType,
    handleZapierAttendeeSync,
    isAdmin,
    isLoading,
    isZapierSyncing,
    organisationProfile,
    perms,
    saved,
    setShowErrorModal,
    showErrorModal,
    toggleFundraising,
    togglePremium,
    urlEventId,
    validationRules,
    zapierSyncMessage,
  } = useEventEditor()

  if (
    createEventStage?.stage === CreateEventStage.Type ||
    (!urlEventId &&
      !formData?.eventId &&
      formData?.event?.eventType === undefined)
  ) {
    return (
      <Box
        className={`flex flex-col items-center mx-auto`}
        style={{ width: '90%' }}
      >
        <Text className={`text-2xl my-2`}>Create your event</Text>
        {eventTypeError && (
          <Box ignoreTheme className="mb-4 text-sm font-semibold text-red-600">
            {eventTypeError}
          </Box>
        )}
        <Box
          className={`flex flex-row justify-evenly space-x-2 overflow-x-auto`}
        >
          <Card
            selected={formData?.event?.eventType === EventType.Online}
            imgSrc={'/images/onlineEvent.svg'}
            imgWidth={200}
            imgHeight={200}
            onClick={() => handleSelectEventType(EventType.Online)}
            disabled={isLoading}
            text="Create online events and engage with your audience all around the world."
            // disabled={!isAdmin && !perms.find((p) => p === 'ONLINE')}
            title={'Online Event'}
            show={false}
          >
            <CheckboxInput
              isProtected
              value={formData?.fundraising?.enabled}
              onChange={(val) => {
                toggleFundraising(val)
              }}
              show={
                (isAdmin || !!perms.find((p) => p === 'FUNDRAISER')) &&
                formData?.event?.eventType === EventType.Online
              }
              className="bg-transparent rounded text-sm border-box -mt-1"
              border=""
              label="Additional Tickets"
            >
              <span className="mt-2">Enable Fundraising</span>
            </CheckboxInput>
            <CheckboxInput
              isProtected
              value={formData?.isPremium}
              onChange={(val) => {
                togglePremium(val)
              }}
              show={
                (isAdmin || !!perms.find((p) => p === 'PREMIUM')) &&
                formData?.event?.eventType === EventType.Online
              }
              className="bg-transparent rounded text-sm border-box -mt-1"
              border=""
              label="Additional Tickets"
            >
              <span className="mt-2">Premium Event</span>
            </CheckboxInput>
          </Card>
          <Card
            selected={formData?.event?.eventType === EventType.InPerson}
            imgSrc={'/images/inpersonEvent.svg'}
            imgWidth={200}
            imgHeight={200}
            onClick={() => {
              handleSelectEventType(EventType.InPerson)
            }}
            disabled={isLoading}
            text=""
            title={'Click to begin'}
          >
            <CheckboxInput
              isProtected
              value={formData?.fundraising?.enabled}
              onChange={(val) => {
                toggleFundraising(val)
              }}
              // show={
              //   (isAdmin || !!perms.find((p) => p === 'FUNDRAISER')) &&
              //   formData?.event?.eventType === EventType.InPerson
              // }
              //
              show={false}
              className="bg-transparent rounded text-sm border-box -mt-1 flex-"
              border=""
              label="Additional Tickets"
            >
              <span className="mt-2">Enable Fundraising</span>
            </CheckboxInput>
            <CheckboxInput
              isProtected
              value={formData?.isPremium}
              onChange={(val) => {
                togglePremium(val)
              }}
              show={
                (isAdmin || !!perms.find((p) => p === 'PREMIUM')) &&
                formData?.event?.eventType === EventType.InPerson
              }
              className="bg-transparent rounded text-sm border-box -mt-1"
              border=""
              label="Additional Tickets"
            >
              <span className="mt-2">Premium Event</span>
            </CheckboxInput>
          </Card>
          <Card
            selected={formData?.event?.eventType === EventType.Fundraiser}
            imgSrc={'/images/fundraisingOnly.svg'}
            imgWidth={200}
            imgHeight={200}
            onClick={() => handleSelectEventType(EventType.Fundraiser)}
            text="Create fundraisers and achieve your goals"
            disabled={
              isLoading || (!isAdmin && !perms.find((p) => p === 'FUNDRAISER'))
            }
            title={'Fundraising Only'}
            show={false}
          ></Card>
        </Box>
      </Box>
    )
  }

  const internalLocationsOptions = (organisationProfile?.locations ?? []).map(
    (loc) => ({ label: loc, value: loc })
  )

  const error_modal = () => {
    return (
      <Modal
        title={'Editor Error'}
        show={showErrorModal}
        setShow={setShowErrorModal}
      >
        <Box className={`flex items-center justify-center pb-4`}>
          <Box
            className={`flex items-center justify-center p-2 bg-red-50 rounded-full`}
          >
            <CiWarning className="w-10 h-10" />
          </Box>
        </Box>
        <span className="font-bold mb-2">
          There was an issue editing the event.
        </span>
        <br />
        <span className="font-bold text-md">Section: </span>
        {/* <span className="text-sm">{errors?.step}</span> */}
        <br />
        <span className="font-bold text-md">Error: </span>
        {/* <span className="text-sm">{errors?.message}</span> */}
      </Modal>
    )
  }

  return (
    <Box
      className={`flex flex-col space-y-6 ${
        createEventStage?.stage === CreateEventStage.Preview && 'h-screen'
      }`}
    >
      {error_modal()}
      {errors && errors.length > 0 ? (
        <span className={`transition-all text-red-500 text-lg font-bold`}>
          Please complete all required sections.
        </span>
      ) : (
        <></>
      )}
      <Box
        show={saved}
        className={`flex items-center justify-end text-forumm-green animate-pulse sticky top-[20px]`}
        ignoreTheme
      >
        <FaRegCheckCircle />
        <Text ignoreTheme className="ml-2">
          Event saved.
        </Text>
      </Box>
      {createEventStage?.stage === CreateEventStage.Details ? (
        <>
          <Accordion
            className={'mt-6'}
            section={CreateEventSection.DETAILS}
            title={
              formData?.event?.eventType === EventType.Fundraiser
                ? 'Fundraising Page'
                : 'Event'
            }
            initiallyOpen={true}
            error={errors?.find((e) => e.step === 'event')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('event'))
            )}
          >
            <DetailsForm />
          </Accordion>

          <Accordion
            className={'mt-6'}
            title={`Registration`}
            section={CreateEventSection.REGISTRATION}
            initiallyOpen={formState.registration}
            error={errors?.find((e) => e.step === 'tickets')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('tickets'))
            )}
          >
            {<TicketsForm currency={organisationProfile.currency} />}
          </Accordion>

          {/* Disabled Fundraising - https://448-studio.atlassian.net/browse/FRM-920 */}
          {/* <Accordion */}
          {/*   title={`Fundraising`} */}
          {/*   section={CreateEventSection.FUNDRAISING} */}
          {/*   className={'mt-6'} */}
          {/*   initiallyOpen={formState.fundraising} */}
          {/*   error={errors?.find((e) => e.step === 'fundraising')?.message} */}
          {/*   show={ */}
          {/*     (shouldShowField( */}
          {/*       validationRules.find((f) => f[0].includes('fundraising')) */}
          {/*     ) || */}
          {/*       !!formData?.fundraising?.enabled) && */}
          {/*     formData?.event?.eventType === 'FUNDRAISER' */}
          {/*   } */}
          {/* > */}
          {/*   {<FundraisingForm currency={organisationProfile.currency} />} */}
          {/* </Accordion> */}

          {/* Tihs is the fundraiser picker only in user for online and in-person */}
          {/* <Accordion */}
          {/*   title={`Fundraising`} */}
          {/*   section={CreateEventSection.FUNDRAISING} */}
          {/*   className={'mt-6'} */}
          {/*   initiallyOpen={formState.fundraising} */}
          {/*   error={errors?.find((e) => e.step === 'fundraising')?.message} */}
          {/*   show={formData?.event?.eventType !== 'FUNDRAISER'} */}
          {/* > */}
          {/*   { */}
          {/*     <FundraisingPickerForm */}
          {/*       currency={organisationProfile.currency} */}
          {/*       organisation={profile?.company ?? profile?.university!} */}
          {/*     /> */}
          {/*   } */}
          {/* </Accordion> */}

          <Accordion
            className={'mt-6'}
            title={`Speakers`}
            section={CreateEventSection.SPEAKERS}
            initiallyOpen={formState.speakers}
            error={errors?.find((e) => e.step === 'speakers')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('speakers'))
            )}
          >
            {<SpeakersForm />}
          </Accordion>

          <Accordion
            className={'mt-6'}
            title={`Stages`}
            section={CreateEventSection.STAGES}
            initiallyOpen={formState.stages}
            error={errors?.find((e) => e.step === 'stages')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('stages'))
            )}
          >
            {<StagesForm />}
          </Accordion>

          <Accordion
            className={'mt-6'}
            title={`Session`}
            section={CreateEventSection.SESSIONS}
            initiallyOpen={formState.sessions}
            error={errors?.find((e) => e.step === 'sessions')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('sessions'))
            )}
          >
            {<SessionsForm />}
          </Accordion>

          <Accordion
            className={'mt-6'}
            title={`Attendees`}
            section={CreateEventSection.ATTENDEES}
            initiallyOpen={formState.attendees}
            error={errors?.find((e) => e.step === 'attendees')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('attendees'))
            )}
          >
            {<AttendeesForm updateInProgress={isLoading} />}
          </Accordion>

          <Accordion
            className={'mt-6'}
            title={`Partners`}
            section={CreateEventSection.PARTNERS}
            initiallyOpen={formState.sponsors}
            error={errors?.find((e) => e.step === 'sponsors')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('sponsors'))
            )}
          >
            {<SponsorsForm />}
          </Accordion>

          {/* Only shows on Fundraiser events */}
          <Accordion
            title={`Communications`}
            section={CreateEventSection.COMMUNICATIONS}
            className={'mt-6'}
            initiallyOpen={formState.communications}
            error={errors?.find((e) => e.step === 'communications')?.message}
            show={
              formData?.event?.eventType === EventType.Fundraiser &&
              shouldShowField(
                validationRules.find((f) => f[0].includes('communications'))
              )
            }
          >
            {<CommunicationsForm />}
          </Accordion>
        </>
      ) : (
        <></>
      )}
      {createEventStage?.stage === CreateEventStage.Communications ? (
        <>
          <Accordion
            title={`Breakout`}
            section={CreateEventSection.BREAKOUT_ROOMS}
            className={'mt-6'}
            initiallyOpen={formState.breakoutRooms}
            error={errors?.find((e) => e.step === 'breakoutRooms')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('breakoutRooms'))
            )}
          >
            {<BreakoutRoomsForm />}
          </Accordion>

          <Accordion
            title={`Communications`}
            section={CreateEventSection.COMMUNICATIONS}
            className={'mt-6'}
            initiallyOpen={formState.communications}
            error={errors?.find((e) => e.step === 'communications')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('communications'))
            )}
          >
            {<CommunicationsForm />}
          </Accordion>

          <Accordion
            title={`Event Emails`}
            section={CreateEventSection.NOTIFICATIONS}
            className={'mt-6'}
            initiallyOpen={formState.notifications}
            error={errors?.find((e) => e.step === 'notifications')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('notifications'))
            )}
          >
            {<NotificationsForm />}
          </Accordion>

          <Accordion
            title={`On - Demand`}
            section={CreateEventSection.ON_DEMAND_CONTENT}
            className={'mt-6'}
            initiallyOpen={formState.onDemand}
            error={errors?.find((e) => e.step === 'ondemandContent')?.message}
            show={shouldShowField(
              validationRules.find((f) => f[0].includes('ondemandContent'))
            )}
          >
            {<OndemandContantForm />}
          </Accordion>
        </>
      ) : (
        <></>
      )}
      {createEventStage?.stage === CreateEventStage.Preview ? (
        <Box className="lg:block w-full relative h-full border-forumm-menu-border">
          <Box
            ignoreTheme
            className="text-md absolute p-2 bg-white rounded text-black opacity-60"
          >
            Preview
          </Box>
          <iframe
            className="w-full h-full rounded border border-white border-solid border-opacity-25 "
            onClick={(e) => {
              e.preventDefault()
            }}
            /* src={`/donation/${formData?.event?.donationUrl}`} */

            src={`/event/${
              formData?.eventId
            }/agenda?preview=true&r=${Math.random()}`}
          />
        </Box>
      ) : (
        <></>
      )}

      {activeEventId && (formData?.attendees?.length ?? 0) > 0 && (
        <Box className="mt-6 flex flex-col items-end gap-2">
          <Button
            title="Sync attendees with Zapier"
            type="secondary"
            icon={<FaSyncAlt />}
            loading={isZapierSyncing || isLoading}
            disabled={isZapierSyncing || isLoading}
            onClick={handleZapierAttendeeSync}
            size="small"
            confirmationModal={{
              title: 'Sync attendees with Zapier',
              content:
                'This sends every current attendee on this event to active Zapier hooks.',
            }}
          />
          {zapierSyncMessage && (
            <Text ignoreTheme className="text-sm text-forumm-green">
              {zapierSyncMessage}
            </Text>
          )}
        </Box>
      )}

      <Box className={`mt-6 ml-auto flex`}>
        <Button
          title="Back to dashboard"
          type="secondary"
          textColor="#3763e9"
          onClick={goToDashboard}
          size="small"
        />
        {completeSections.details && (
          <Button
            data-testid={'submit-button'}
            className={`ml-2`}
            title={
              createEventStage?.stage === CreateEventStage.Preview
                ? 'Finish'
                : createEventStage?.stage === CreateEventStage.Communications &&
                    createEventStage.skipPreview
                  ? 'Finish'
                  : 'Save & Next'
            }
            buttonType="submit"
            onClick={handleNext}
            size="small"
          />
        )}
      </Box>
    </Box>
  )
}

CreateEventPage.Layout = CreateEventLayout
